import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { Bell, CheckCheck, Crown, KeyRound, LifeBuoy, Mail } from "lucide-react";
import { toast } from "sonner";
import { Spinner } from "@/components/catalog/TitleGrid";
import { api, formatApiError } from "@/lib/api";
import { usePlatform } from "@/lib/platform";

export function NotificationsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const load = () => api.get("/notifications").then((r) => setItems(r.data.items || r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const readAll = async () => { await api.post("/notifications/read-all"); load(); };
  const open = async (n) => { if (!n.read) await api.post(`/notifications/${n.id}/read`).catch(() => {}); navigate(n.link || "/"); };
  if (!items) return <div className="pt-32"><Spinner /></div>;
  return (
    <div data-testid="notifications-page" className="mx-auto max-w-3xl px-4 pt-32 pb-16">
      <div className="flex flex-wrap items-end justify-between gap-3"><h1 className="font-display text-3xl font-bold gold-text sm:text-4xl">Notifications</h1><button data-testid="notifications-page-read-all" onClick={readAll} className="btn-ghost-gold px-4 py-2 text-xs"><CheckCheck className="h-4 w-4" /> Mark all read</button></div>
      <div className="mt-8 space-y-2">
        {items.map((n) => (
          <button key={n.id} data-testid={`notification-row-${n.id}`} onClick={() => open(n)} className={`flex w-full items-start gap-4 rounded-xl border p-4 text-left transition-colors ${n.read ? "border-white/5 bg-[#0B0B0D]/60" : "border-[#D4AF37]/30 bg-[#0B0B0D]"} hover:border-[#D4AF37]/60`}>
            {n.image ? <img src={n.image} alt="" className="h-14 w-24 flex-none rounded object-cover" /> : <Bell className="h-6 w-6 flex-none text-[#D4AF37]" />}
            <span className="min-w-0 flex-1"><span className="flex items-center gap-2">{!n.read && <span className="h-2 w-2 rounded-full bg-[#E50914]" />}<span className="font-semibold text-white">{n.title}</span></span><span className="mt-1 block text-sm text-[#8B8B8B]">{n.body}</span><span className="mt-1 block text-xs text-[#D4AF37]/70">{formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}</span></span>
          </button>
        ))}
        {!items.length && <p className="text-center text-white/50">You're all caught up.</p>}
      </div>
    </div>
  );
}

const PAGES = {
  about: { icon: Crown, title: "About AETRYX", body: ["AETRYX AI Studios is a cinematic streaming universe. Watch black-and-gold Originals, then build your own stories with AI tools for scripts, characters, storyboards and posters.", "Creators can submit finished work for AETRYX review and prepare distribution-ready packages for their own pitches. AETRYX does not publish directly to third-party streaming platforms.", "Payments run in Stripe test mode during the preview."] },
  support: { icon: LifeBuoy, title: "Support", body: ["Billing: manage your plan and payment history in Account → Billing. Cancelling keeps access until the end of your period.", "Playback: premium titles need a Creator or Studio plan. Kids profiles only show family-rated content.", "Create Studio: failed AI generations are refunded automatically — check the credit ledger in Account.", "Forgot your password? Use “Forgot password?” on the sign-in page."], contact: "support@aetryx.ai" },
};

export function InfoPage({ page }) {
  const p = PAGES[page];
  const support = usePlatform();
  return (
    <div data-testid={`info-page-${page}`} className="mx-auto max-w-3xl px-4 pt-36 pb-16">
      <p.icon className="h-10 w-10 text-[#D4AF37]" strokeWidth={1.4} />
      <h1 className="mt-4 font-display text-4xl sm:text-5xl font-bold gold-text">{p.title}</h1>
      <div className="mt-8 space-y-5 text-white/75 leading-relaxed">{p.body.map((t) => <p key={t}>{t}</p>)}</div>
      {p.contact && <a href={`mailto:${support.support_email || p.contact}`} data-testid="support-email" className="btn-ghost-gold mt-8 inline-flex px-6 py-3 text-sm"><Mail className="h-4 w-4" /> {support.support_email || p.contact}</a>}
      {page === "support" && <SupportForm />}
    </div>
  );
}

function SupportForm() {
  const [f, setF] = useState({ name: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/support/contact", f); setSent(true); } catch (x) { toast.error(formatApiError(x)); }
  };
  const c = "w-full rounded-lg border border-[#D4AF37]/25 bg-[#050505] px-3 py-2.5 text-sm text-white focus:border-[#FFD86A] focus:outline-none";
  if (sent) return <p data-testid="support-sent" className="mt-10 rounded-xl border border-[#D4AF37]/30 p-5 text-sm text-[#FFD86A]">Thanks — your message reached the AETRYX support team. We'll reply by email.</p>;
  return (
    <form data-testid="support-form" onSubmit={submit} className="mt-10 space-y-3 panel p-6">
      <h2 className="eyebrow">Contact support</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        <input data-testid="support-name" required value={f.name} onChange={set("name")} placeholder="Your name" className={c} />
        <input data-testid="support-email-input" required type="email" value={f.email} onChange={set("email")} placeholder="Email" className={c} />
      </div>
      <input data-testid="support-subject" required value={f.subject} onChange={set("subject")} placeholder="Subject" className={c} />
      <textarea data-testid="support-message" required minLength={5} rows={5} value={f.message} onChange={set("message")} placeholder="How can we help?" className={c} />
      <button data-testid="support-submit" className="btn-gold px-6 py-3 text-sm">Send message</button>
    </form>
  );
}

const Shell = ({ children, testId }) => (
  <div data-testid={testId} className="flex min-h-screen items-center justify-center bg-[#050505] px-4">
    <div className="w-full max-w-md panel p-8"><KeyRound className="h-9 w-9 text-[#D4AF37]" />{children}</div>
  </div>
);

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [res, setRes] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    try { setRes((await api.post("/auth/forgot-password", { email, origin_url: window.location.origin })).data); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Shell testId="forgot-password-page">
      <h1 className="mt-4 font-display text-3xl gold-text">Reset password</h1>
      {res ? <div data-testid="forgot-sent" className="mt-4 space-y-3 text-sm text-white/75"><p>{res.message}</p>{res.dev_reset_url && <p className="rounded-lg border border-[#00F0FF]/30 p-3 text-xs text-[#7CF7FF]">Test mode (no email service yet): <a data-testid="dev-reset-link" href={res.dev_reset_url} className="break-all underline">open reset link</a></p>}</div>
        : <form onSubmit={submit} className="mt-6 space-y-4"><input data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="w-full rounded-lg border border-[#D4AF37]/25 bg-black px-4 py-3 text-white focus:border-[#FFD86A] focus:outline-none" /><button data-testid="forgot-submit" className="btn-gold w-full py-3 text-sm">Send reset link</button></form>}
      <Link to="/login" className="mt-6 block text-center text-sm text-[#FFD86A] hover:underline">Back to sign in</Link>
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/reset-password", { token: params.get("token") || "", password: pw }); toast.success("Password updated — sign in with your new password"); navigate("/login"); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Shell testId="reset-password-page">
      <h1 className="mt-4 font-display text-3xl gold-text">Choose a new password</h1>
      <form onSubmit={submit} className="mt-6 space-y-4"><input data-testid="reset-password-input" type="password" minLength={8} required value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" className="w-full rounded-lg border border-[#D4AF37]/25 bg-black px-4 py-3 text-white focus:border-[#FFD86A] focus:outline-none" /><button data-testid="reset-submit" className="btn-gold w-full py-3 text-sm">Update password</button></form>
    </Shell>
  );
}
